/**
 * RecordCategoriesTab — Ustawienia kartoteki systemowej → zakładka „Kategorie".
 *
 * CRUD listy `record_categories` (NOWA oś klasyfikacji, JEDNA kategoria na
 * rekord, tabela realna — migracja 0123, `apps/api/lib/records/categories.ts`)
 * dla danej kartoteki (`kartotekaKey`, np. `contractors`). Renderowana ze wspólnego panelu
 * ustawień „Kategorie" (`RecordCategoriesSettingsTab`) osobno per kartoteka z listy
 * `RECORD_CATEGORY_KARTOTEKI` (`lib/record-categories.ts`; dziś: kontrahenci i dokumenty).
 *
 * ŚWIADOMIE prostszy, NOWY odpowiednik starego `SystemModuleCategoriesTab`
 * (`system_module_categories`) — bez koloru, bez `fieldOverrides`, bez
 * widoczności per-rola. Tylko nazwa (+ opcjonalna kolejność).
 *
 * Backend: `GET/POST /api/record-categories`, `PATCH/DELETE /api/record-categories/:id`
 * (patrz `apps/api/app/api/record-categories/`). Permission mutacji:
 * `crm_core.system_modules.manage` — egzekwowane przez backend (403 → toast);
 * brak dodatkowej bramki po stronie tego komponentu, spójnie z sąsiednią
 * zakładką „Pola" (`SystemModuleFieldsPanel`, też bez client-side gate).
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.confirm/alert — ConfirmDialog +
 * useToast, ZERO surowych <button>/<input> — Button/IconButton/Input.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import type { RecordCategory } from '@/components/record-categories/RecordCategoryPicker';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { RecordCategoryFormModal, type RecordCategoryFormValues } from './RecordCategoryFormModal';

export interface RecordCategoriesTabProps {
  kartotekaKey: string;
}

export function RecordCategoriesTab({ kartotekaKey }: RecordCategoriesTabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<RecordCategory | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RecordCategory | null>(null);

  const queryKey = ['record-categories', kartotekaKey];

  const categoriesQ = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get<{ categories: RecordCategory[] }>('/record-categories', {
        params: { kartoteka: kartotekaKey },
      });
      return res.data.categories;
    },
  });

  const categories = categoriesQ.data ?? [];

  function invalidate() {
    queryClient.invalidateQueries({ queryKey });
  }

  function handleMutationError(err: unknown) {
    const code = isAxiosError(err)
      ? ((err.response?.data as { error?: string } | undefined)?.error ?? null)
      : null;
    if (code === 'CATEGORY_LIMIT_EXCEEDED') {
      toast.show({ variant: 'warning', title: t('tenant.recordCategories.toast.maxReached') });
      return;
    }
    if (code === 'CATEGORY_NAME_DUPLICATE') {
      toast.show({ variant: 'error', title: t('tenant.recordCategories.toast.duplicateName') });
      return;
    }
    if (code === 'VALIDATION_ERROR') {
      toast.show({ variant: 'error', title: t('tenant.recordCategories.toast.validationError') });
      return;
    }
    if (code === 'FORBIDDEN') {
      toast.show({ variant: 'error', title: t('tenant.recordCategories.toast.forbidden') });
      return;
    }
    toast.show({ variant: 'error', title: t('tenant.recordCategories.toast.saveError') });
  }

  const createMutation = useMutation({
    mutationFn: async (values: RecordCategoryFormValues) => {
      const res = await api.post<{ category: RecordCategory }>('/record-categories', {
        kartotekaKey,
        name: values.name,
        sortOrder: values.sortOrder,
      });
      return res.data.category;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.recordCategories.toast.created') });
      setCreateOpen(false);
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: RecordCategoryFormValues }) => {
      const res = await api.patch<{ category: RecordCategory }>(`/record-categories/${id}`, {
        name: values.name,
        sortOrder: values.sortOrder,
      });
      return res.data.category;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.recordCategories.toast.updated') });
      setEditing(null);
    },
    onError: handleMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/record-categories/${id}`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.recordCategories.toast.deleted') });
      setPendingDelete(null);
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.recordCategories.toast.deleteError') });
      setPendingDelete(null);
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (categoriesQ.isLoading) {
    return (
      <div
        className="flex items-center justify-center py-10"
        data-testid="record-categories-tab-loading"
      >
        <Spinner />
      </div>
    );
  }

  const sorted = [...categories].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'pl'),
  );

  return (
    <div data-testid="record-categories-tab">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.recordCategories.tab.title')}
          </h2>
          <p className="text-xs text-text-tertiary">
            {t('tenant.recordCategories.tab.description')}
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
          data-testid="record-categories-add-btn"
        >
          {t('tenant.recordCategories.tab.addButton')}
        </Button>
      </header>

      {sorted.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary" data-testid="record-categories-empty">
            {t('tenant.recordCategories.tab.empty')}
          </p>
        </Card>
      ) : (
        <Card padding="none">
          <ul className="divide-y divide-border-primary">
            {sorted.map((cat) => (
              <li
                key={cat.id}
                className="flex items-center gap-3 px-4 py-2.5"
                data-testid={`record-categories-row-${cat.id}`}
              >
                <span className="flex-1 text-sm text-text-primary">{cat.name}</span>
                <IconButton
                  aria-label={t('common.edit')}
                  size="sm"
                  onClick={() => setEditing(cat)}
                  disabled={isSaving || deleteMutation.isPending}
                  data-testid={`record-categories-edit-${cat.id}`}
                >
                  <Pencil aria-hidden="true" />
                </IconButton>
                <IconButton
                  aria-label={t('common.delete')}
                  size="sm"
                  variant="danger"
                  onClick={() => setPendingDelete(cat)}
                  disabled={isSaving || deleteMutation.isPending}
                  data-testid={`record-categories-delete-${cat.id}`}
                >
                  <Trash2 aria-hidden="true" />
                </IconButton>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <RecordCategoryFormModal
        open={createOpen || editing !== null}
        mode={editing ? 'edit' : 'add'}
        initialValues={
          editing
            ? { name: editing.name, sortOrder: editing.sortOrder }
            : { name: '', sortOrder: sorted.length }
        }
        saving={isSaving}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
        onSubmit={(values) => {
          if (editing) {
            updateMutation.mutate({ id: editing.id, values });
          } else {
            createMutation.mutate(values);
          }
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.recordCategories.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.recordCategories.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
